(function() {
  const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  const LEAFLET_CSS_INTEGRITY = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
  const LEAFLET_JS_INTEGRITY = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
  const GEOCODE_CACHE_PREFIX = 'jomo-delivery-geocode:v2:';
  const IP_LOCATION_CACHE_KEY = 'jomo-delivery-ip-location-v2';
  const IP_LOCATION_MAX_AGE = 12 * 60 * 60 * 1000;
  const VISIBLE_RATE_COUNT = 4;
  const PARCEL_ORDER_CUTOFF = 'Order by Saturday night to ship on the next dispatch date.';
  const CANADA_ONLY_TITLE = 'We deliver and ship within Canada.';
  const CANADA_ADDRESS_PROMPT = 'Enter a Canadian address or postal code to check the available delivery and shipping options.';
  const TERRITORY_ADDRESS_PROMPT = 'We ship to all 10 Canadian provinces. Enter a different address or postal code to check your options.';
  const PROVINCE_ALIASES = {
    AB: 'Alberta', ALBERTA: 'Alberta',
    BC: 'British Columbia', BRITISHCOLUMBIA: 'British Columbia',
    MB: 'Manitoba', MANITOBA: 'Manitoba',
    NB: 'New Brunswick', NEWBRUNSWICK: 'New Brunswick',
    NL: 'Newfoundland and Labrador', NEWFOUNDLANDANDLABRADOR: 'Newfoundland and Labrador',
    NS: 'Nova Scotia', NOVASCOTIA: 'Nova Scotia',
    NT: 'Northwest Territories', NWT: 'Northwest Territories', NORTHWESTTERRITORIES: 'Northwest Territories',
    NU: 'Nunavut', NUNAVUT: 'Nunavut',
    ON: 'Ontario', ONTARIO: 'Ontario',
    PE: 'Prince Edward Island', PEI: 'Prince Edward Island', PRINCEEDWARDISLAND: 'Prince Edward Island',
    QC: 'Quebec', PQ: 'Quebec', QUEBEC: 'Quebec',
    SK: 'Saskatchewan', SASKATCHEWAN: 'Saskatchewan',
    YT: 'Yukon', YUKON: 'Yukon'
  };

  let leafletPromise;

  function loadLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;

    leafletPromise = new Promise((resolve, reject) => {
      if (!document.querySelector(`link[href="${LEAFLET_CSS_URL}"]`)) {
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = LEAFLET_CSS_URL;
        stylesheet.integrity = LEAFLET_CSS_INTEGRITY;
        stylesheet.crossOrigin = '';
        document.head.appendChild(stylesheet);
      }

      const existingScript = document.querySelector(`script[src="${LEAFLET_JS_URL}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.L), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = LEAFLET_JS_URL;
      script.integrity = LEAFLET_JS_INTEGRITY;
      script.crossOrigin = '';
      script.onload = () => resolve(window.L);
      script.onerror = () => reject(new Error('Leaflet failed to load'));
      document.head.appendChild(script);
    });

    return leafletPromise;
  }

  class DeliveryZoneMap extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedAndInitialized) return;
      this.isConnectedAndInitialized = true;
      const generation = (this.lifecycleGeneration || 0) + 1;
      this.lifecycleGeneration = generation;
      this.lifecycleController = new AbortController();
      this.listenerCleanups = [];
      this.selectionRevision = 0;
      this.userAdjustedViewport = false;

      this.canvas = this.querySelector('[data-zone-canvas]');
      this.loading = this.querySelector('[data-zone-loading]');
      this.fallback = this.querySelector('[data-zone-fallback]');
      this.form = this.querySelector('[data-zone-form]');
      this.input = this.querySelector('[data-zone-input]');
      this.submitButton = this.querySelector('[data-zone-submit]');
      this.locationButton = this.querySelector('[data-zone-location]');
      this.clearButton = this.querySelector('[data-zone-clear]');
      this.result = this.querySelector('[data-zone-result]');
      this.resultTitle = this.querySelector('[data-zone-result-title]');
      this.resultDispatch = this.querySelector('[data-zone-result-dispatch]');
      this.resultCopy = this.querySelector('[data-zone-result-copy]');
      this.rates = this.querySelector('[data-zone-rates]');
      this.ratesTitle = this.querySelector('[data-zone-rates-title]');
      this.ratesCopy = this.querySelector('[data-zone-rates-copy]');
      this.ratesList = this.querySelector('[data-zone-rates-list]');
      this.ratesToggle = this.querySelector('[data-zone-rates-toggle]');
      this.ratesToggleLabel = this.querySelector('[data-zone-rates-toggle-label]');
      this.featureLayers = new Map();
      this.provinceLayers = new Map();
      this.parcelResultCopy = null;

      this.dataLoadStarted = false;
      this.dataReady = false;
      this.setSearching(false);
      this.rates?.classList.toggle('is-loading', false);
      if (this.canvas) this.canvas.hidden = false;
      if (this.fallback) this.fallback.hidden = true;
      this.listen(this.ratesToggle, 'click', () => this.toggleAdditionalRates());
      this.listen(window, 'jomo:shipment-schedule-change', () => this.refreshShipmentSchedule());
      this.listen(window, 'jomo:delivery-zone-change', (event) => this.receiveSelectedCoverage(event.detail));

      // A failed data load can be retried without leaving an unusable form.
      this.listen(this.form, 'submit', async (event) => {
        if (this.dataReady) return;
        event.preventDefault();
        if (this.dataLoadStarted) return;
        const query = this.input?.value.trim();
        await this.loadData(generation);
        if (this.isCurrentGeneration(generation) && this.dataReady && query) this.searchAddress(query);
      });
      const disclosure = this.dataset.loadOnOpen === 'true' ? this.closest('details') : null;
      this.watchCheckerVisibility(disclosure);
      if (disclosure) {
        this.listen(disclosure, 'toggle', () => {
          if (disclosure.open) {
            this.trackCheckerOpened('accordion');
            this.loadData(generation);
          } else {
            this.analyticsOpenTracked = false;
          }
        });
        if (!disclosure.open) return;
      }
      this.loadData(generation);
    }

    loadData(generation = this.lifecycleGeneration) {
      if (!this.isCurrentGeneration(generation)) return;
      if (this.dataLoadStarted) return this.dataLoadPromise;
      this.dataLoadStarted = true;
      this.setSearching(true);
      if (this.result) this.result.hidden = true;
      this.dataLoadPromise = Promise.all([
        window.JomoDeliveryData.loadJson(this.dataset.zonesUrl),
        window.JomoDeliveryData.loadJson(this.dataset.canadaZonesUrl)
      ])
        .then(([zones, canadaZones]) => {
          if (!this.isCurrentGeneration(generation)) return null;
          this.zones = zones;
          this.canadaZones = canadaZones;
          try {
            const cached = JSON.parse(sessionStorage.getItem(IP_LOCATION_CACHE_KEY));
            if (cached?.savedAt && Date.now() - cached.savedAt < IP_LOCATION_MAX_AGE) this.rememberDetectedLocation(cached.location, cached.savedAt);
          } catch (_error) {}
          this.dataReady = true;
          this.setSearching(false);
          this.bindChecker();
          return this.dataset.showMap === 'true' ? loadLeaflet() : null;
        })
        .then((leaflet) => {
          if (!this.isCurrentGeneration(generation)) return;
          if (leaflet) {
            this.initializeMap();
          } else if (this.form) {
            this.setInitialViewport(generation);
          }
        })
        .catch((error) => {
          if (!this.isCurrentGeneration(generation) || error?.name === 'AbortError') return;
          if (!this.dataReady) {
            this.dataLoadStarted = false;
            this.setSearching(false);
            if (this.locationButton) this.locationButton.disabled = true;
            this.showResult('error', 'Delivery checker could not load', 'Enter your address or postal code and check again to retry.');
          }
          console.error('[Delivery zone map]', error);
          this.showMapFallback();
        });
      return this.dataLoadPromise;
    }

    disconnectedCallback() {
      this.isConnectedAndInitialized = false;
      this.lifecycleGeneration = (this.lifecycleGeneration || 0) + 1;
      this.lifecycleController?.abort();
      this.listenerCleanups?.forEach((cleanup) => cleanup());
      this.listenerCleanups = [];
      this.analyticsVisibilityObserver?.disconnect();
      this.analyticsVisibilityObserver = null;
      this.parcelResultCopy = null;
      this.mapResizeObserver?.disconnect();
      this.mapResizeObserver = null;
      if (this.mapResizeFrame) cancelAnimationFrame(this.mapResizeFrame);
      this.mapResizeFrame = null;
      if (this.mapInvalidateTimer) clearTimeout(this.mapInvalidateTimer);
      this.mapInvalidateTimer = null;
      this.searchMarker?.remove();
      this.searchMarker = null;
      if (this.map) this.map.remove();
      this.map = null;
      this.canadaLayer = null;
      this.zoneLayer = null;
      this.featureLayers?.clear();
      this.provinceLayers?.clear();
    }

    isCurrentGeneration(generation) {
      return this.isConnectedAndInitialized && this.lifecycleGeneration === generation;
    }

    listen(target, eventName, handler, options) {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      this.listenerCleanups.push(() => target.removeEventListener(eventName, handler, options));
    }

    analyticsContext() {
      let context = {};
      try { context = window.JomoAnalytics?.deliveryContext?.() || {}; } catch (_error) {}
      return { ...context, surface: this.dataset.analyticsSurface || 'unknown' };
    }

    trackCheckerOpened(openTrigger) {
      if (this.analyticsOpenTracked || !['product_page', 'delivery_page'].includes(this.dataset.analyticsSurface)) return;
      this.analyticsOpenTracked = true;
      try {
        window.JomoAnalytics?.deliveryEvent?.('delivery_checker_opened', {
          ...this.analyticsContext(), open_trigger: openTrigger
        });
      } catch (_error) {}
    }

    watchCheckerVisibility(disclosure) {
      if (!this.form || !['product_page', 'delivery_page'].includes(this.dataset.analyticsSurface)) return;
      const generation = this.lifecycleGeneration;
      const visible = () => {
        if (!this.isCurrentGeneration(generation) || (disclosure && !disclosure.open)) return;
        this.trackCheckerOpened('visible');
      };
      if (typeof IntersectionObserver === 'function') {
        this.analyticsVisibilityObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) visible();
        }, { threshold: 0.01 });
        this.analyticsVisibilityObserver.observe(this.form);
      }
      this.listen(this.form, 'focusin', visible);
    }

    rememberDetectedLocation(location, savedAt = location?.detectedAt ?? Date.now()) {
      if (!location?.country) return;
      let estimate;
      if (location.country !== 'CA') {
        estimate = { service: 'outside' };
      } else {
        const hasPoint = Number.isFinite(location.longitude) && Number.isFinite(location.latitude);
        const point = hasPoint ? [location.longitude, location.latitude] : null;
        const zone = point && this.zones?.features.find((feature) => this.featureContainsPoint(feature, point));
        const province = this.findProvinceByHint(location.subdivision)
          || (point && this.canadaZones?.features.find((feature) => this.featureContainsPoint(feature, point)));
        if (zone) estimate = { service: 'local', zoneName: zone.properties.name, provinceName: province?.properties.name || 'Ontario' };
        else if (province) estimate = { service: province.properties.shippable ? 'shipping' : 'territory', provinceName: province.properties.name };
      }
      if (estimate) {
        try { window.JomoDeliveryData?.setEstimate?.({ ...estimate, savedAt }); } catch (_error) {}
      }
    }

    initializeMap() {
      if (!this.canvas || !window.L) throw new Error('Map canvas is unavailable');

      const generation = this.lifecycleGeneration;
      const L = window.L;
      this.map = L.map(this.canvas, {
        zoomControl: false,
        scrollWheelZoom: false,
        preferCanvas: true,
        minZoom: 3,
        maxZoom: 17
      });

      L.control.zoom({ position: 'bottomright' }).addTo(this.map);
      this.map.attributionControl.setPrefix(false);
      L.tileLayer(this.dataset.tileUrl, {
        attribution: this.dataset.tileAttribution,
        maxZoom: 19
      }).addTo(this.map);

      this.canadaLayer = L.geoJSON(this.canadaZones, {
        style: (feature) => this.styleProvince(feature),
        onEachFeature: (feature, layer) => {
          this.provinceLayers.set(feature.properties.name, layer);
          layer.bindPopup(() => this.provincePopupMarkup(feature));
          layer.on({
            click: () => {
              this.clearSelectedZone();
              this.selectProvince(feature.properties.name);
            },
            mouseover: () => layer.setStyle({ weight: 2.4, fillOpacity: feature.properties.shippable ? 0.34 : 0.22 }),
            mouseout: () => this.resetProvinceStyle(feature.properties.name)
          });
        }
      }).addTo(this.map);

      this.zoneLayer = L.geoJSON(this.zones, {
        style: (feature) => this.styleFeature(feature),
        onEachFeature: (feature, layer) => {
          this.featureLayers.set(feature.properties.name, layer);
          layer.bindPopup(this.popupMarkup(feature));
          layer.on({
            click: () => {
              this.clearSelectedProvince();
              this.selectZone(feature.properties.name);
            },
            mouseover: () => layer.setStyle({ weight: 3, fillOpacity: 0.36 }),
            mouseout: () => this.resetLayerStyle(feature.properties.name)
          });
        }
      }).addTo(this.map);

      this.map.fitBounds(this.getShippingCoverageBounds(), this.mapMotionOptions({ padding: [20, 20] }));
      this.userAdjustedViewport = false;
      this.map.on('mousedown touchstart keydown', () => { this.userAdjustedViewport = true; });
      this.map.on('focus', () => this.map.scrollWheelZoom.enable());
      this.map.on('blur', () => this.map.scrollWheelZoom.disable());

      if (this.loading) this.loading.hidden = true;
      this.mapInvalidateTimer = setTimeout(() => {
        this.mapInvalidateTimer = null;
        if (this.isCurrentGeneration(generation)) this.map?.invalidateSize();
      }, 0);
      this.observeMapSize();
      this.setInitialViewport(this.lifecycleGeneration);
    }

    observeMapSize() {
      if (!this.canvas || !('ResizeObserver' in window)) return;
      const generation = this.lifecycleGeneration;

      this.mapResizeObserver = new ResizeObserver(() => {
        if (!this.isCurrentGeneration(generation) || !this.map) return;
        if (this.mapResizeFrame) cancelAnimationFrame(this.mapResizeFrame);

        this.mapResizeFrame = requestAnimationFrame(() => {
          if (this.isCurrentGeneration(generation)) this.map?.invalidateSize({ animate: false, pan: false });
        });
      });
      this.mapResizeObserver.observe(this.canvas);
    }

    styleFeature(feature) {
      const isCore = feature.properties.name === 'Toronto Core';
      return {
        color: this.cssVariable('--delivery-zone-border', '#424242'),
        weight: isCore ? 2.2 : 1.5,
        opacity: 0.92,
        fillColor: this.cssVariable(isCore ? '--delivery-zone-core' : '--delivery-zone-gta', isCore ? '#ffec00' : '#61d4a6'),
        fillOpacity: isCore ? 0.28 : 0.22
      };
    }

    async setInitialViewport(generation = this.lifecycleGeneration) {
      if (!this.isCurrentGeneration(generation) || !this.zones || !this.canadaZones) return;

      try {
        const selectedCoverage = this.getSelectedCoverage();
        if (selectedCoverage && this.restoreSelectedCoverage(selectedCoverage)) return;

        if (!this.userAdjustedViewport && !this.input?.value.trim() && !this.isSearching) {
          this.showResult('info', CANADA_ONLY_TITLE, CANADA_ADDRESS_PROMPT);
        }

        const location = await this.getApproximateLocation(this.lifecycleController?.signal);
        if (!this.isCurrentGeneration(generation)) return;
        this.rememberDetectedLocation(location);
        const latestSelection = this.getSelectedCoverage();
        if (latestSelection && this.restoreSelectedCoverage(latestSelection)) return;
        if (this.userAdjustedViewport || !location?.country || this.input?.value.trim() || this.isSearching) return;

        if (location.country !== 'CA') {
          this.publishApproximateCoverage({ service: 'outside' });
          this.showResult(
            'outside',
            CANADA_ONLY_TITLE,
            CANADA_ADDRESS_PROMPT
          );
          return;
        }

        const point = [location.longitude, location.latitude];
        const localZone = this.zones.features.find((feature) => this.featureContainsPoint(feature, point));
        if (localZone) {
          const layer = this.featureLayers.get(localZone.properties.name);
          this.clearSelectedProvince();
          this.selectZone(localZone.properties.name);
          if (this.map && layer) {
            this.map.fitBounds(layer.getBounds(), { padding: [34, 34], maxZoom: 11, animate: false });
          }
          this.showApproximateCoverage(localZone);
          return;
        }

        const province = this.findProvinceByHint(location.subdivision)
          || this.canadaZones.features.find((feature) => this.featureContainsPoint(feature, point));
        if (!province) return;

        const layer = this.provinceLayers.get(province.properties.name);
        this.clearSelectedZone();
        this.selectProvince(province.properties.name);
        if (this.map && layer && province.properties.shippable) {
          this.map.fitBounds(layer.getBounds(), { padding: [34, 34], maxZoom: 6, animate: false });
        } else if (!province.properties.shippable) {
          this.showAllCanada();
        }
        this.showApproximateCoverage(null, province);
      } catch (error) {
        if (!this.isCurrentGeneration(generation) || error?.name === 'AbortError') return;
        console.info('[Delivery zone map] Using the all-Canada view because approximate location was unavailable.', error);
      }
    }

    showApproximateCoverage(localZone, province = null) {
      if (!this.result || this.userAdjustedViewport || this.input?.value.trim()) return;

      if (localZone) {
        this.publishApproximateCoverage({ service: 'local', zoneName: localZone.properties.name });
        this.showResult(
          'success',
          localZone.properties.name,
          `${localZone.properties.description} Enter your address or postal code to confirm.`
        );
        return;
      }

      if (province?.properties.shippable) {
        this.publishApproximateCoverage({ service: 'shipping', provinceName: province.properties.name });
        this.showResult(
          'success',
          province.properties.name,
          this.parcelEligibilityCopy(true),
          true
        );
        return;
      }

      if (province) {
        this.publishApproximateCoverage({ service: 'territory', provinceName: province.properties.name });
        this.showResult(
          'outside',
          this.territoryMessage(province.properties.name),
          TERRITORY_ADDRESS_PROMPT
        );
      }
    }

    publishApproximateCoverage(selection) {
      // Update the heading without treating an IP estimate as a confirmed address.
      window.dispatchEvent(new CustomEvent('jomo:delivery-zone-estimate', { detail: selection }));
    }

    getSelectedCoverage() {
      return window.JomoDeliveryData.getSelection();
    }

    receiveSelectedCoverage(selection) {
      if (!selection || this.publishingCoverage) return;
      this.sharedCoverage = selection;
      this.selectionRevision = (this.selectionRevision || 0) + 1;
      if (!this.dataReady) return;

      if (this.isSearching) this.setSearching(false);
      if (this.rates) this.rates.hidden = true;
      const draft = this.input?.value.trim();
      // Leave a partially entered address intact; an older completed search can
      // safely yield to the address checked in another surface.
      if (draft && draft !== this.lastCheckedInput) {
        if (this.result) this.result.hidden = true;
        this.parcelResultCopy = null;
        this.renderShipmentDate();
        return;
      }
      if (this.input) this.input.value = '';
      if (this.clearButton) this.clearButton.hidden = true;
      this.searchMarker?.remove();
      this.searchMarker = null;
      this.restoreSelectedCoverage(selection, true);
    }

    refreshSelectedCoverage() {
      if (!this.isConnectedAndInitialized) return;
      if (!this.dataReady) {
        this.loadData();
        return;
      }
      const selection = this.getSelectedCoverage();
      const current = this.sharedCoverage;
      if (selection && (!current || ['service', 'zoneName', 'provinceName', 'savedAt'].some((key) => selection[key] !== current[key]))) {
        this.receiveSelectedCoverage(selection);
      }
    }

    restoreSelectedCoverage(selection, force = false) {
      if (!force && (this.userAdjustedViewport || this.input?.value.trim())) return false;
      this.sharedCoverage = selection;

      if (selection.service === 'local' && selection.zoneName) {
        const zone = this.zones.features.find((feature) => feature.properties.name === selection.zoneName);
        const layer = this.featureLayers.get(selection.zoneName);
        if (!zone) return false;

        this.clearSelectedProvince();
        this.selectZone(selection.zoneName);
        if (this.map && layer) {
          this.map.fitBounds(layer.getBounds(), { padding: [34, 34], maxZoom: 11, animate: false });
        }
        this.showResult('success', selection.zoneName, zone.properties.description);
        return true;
      }

      if ((selection.service === 'shipping' || selection.service === 'territory') && selection.provinceName) {
        const province = this.canadaZones.features.find((feature) => feature.properties.name === selection.provinceName);
        const layer = this.provinceLayers.get(selection.provinceName);
        if (!province) return false;

        this.clearSelectedZone();
        this.selectProvince(selection.provinceName);
        if (this.map && layer && selection.service === 'shipping' && province.properties.shippable) {
          this.map.fitBounds(layer.getBounds(), { padding: [34, 34], maxZoom: 6, animate: false });
        } else if (selection.service === 'territory') {
          this.showAllCanada();
        }

        if (selection.service === 'shipping' && province.properties.shippable) {
          this.showResult(
            'success',
            selection.provinceName,
            this.parcelEligibilityCopy(),
            true
          );
        } else {
          this.showResult(
            'outside',
            this.territoryMessage(selection.provinceName),
            TERRITORY_ADDRESS_PROMPT
          );
        }

        return true;
      }

      if (selection.service === 'territory') {
        this.showAllCanada();
        this.showResult('outside', this.territoryMessage(selection.provinceName), TERRITORY_ADDRESS_PROMPT);
        return true;
      }

      if (selection.service === 'outside') {
        this.clearSelectedZone();
        this.clearSelectedProvince();
        this.showAllCanada();
        this.showResult('outside', CANADA_ONLY_TITLE, CANADA_ADDRESS_PROMPT);
        return true;
      }

      return false;
    }

    async getApproximateLocation(lifecycleSignal = this.lifecycleController?.signal) {
      if (!this.dataset.ipGeocoderUrl) return null;

      try {
        const cached = JSON.parse(sessionStorage.getItem(IP_LOCATION_CACHE_KEY));
        if (cached && cached.savedAt && Date.now() - cached.savedAt < IP_LOCATION_MAX_AGE) return { ...cached.location, detectedAt: cached.savedAt };
      } catch (_error) {
        // Session storage may be disabled; continue with a one-time request.
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);
      const abortForLifecycle = () => controller.abort();
      if (lifecycleSignal?.aborted) {
        controller.abort();
      } else {
        lifecycleSignal?.addEventListener('abort', abortForLifecycle, { once: true });
      }

      try {
        const response = await fetch(this.dataset.ipGeocoderUrl, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Approximate location service returned ${response.status}`);

        const data = await response.json();
        const latitude = Number(data.location?.latitude ?? data.latitude);
        const longitude = Number(data.location?.longitude ?? data.longitude);
        const location = {
          country: String(data.country || data.country_code || '').toUpperCase(),
          subdivision: data.subdivision || data.region || '',
          latitude,
          longitude,
          detectedAt: Date.now()
        };

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

        try {
          sessionStorage.setItem(IP_LOCATION_CACHE_KEY, JSON.stringify({ location, savedAt: location.detectedAt }));
        } catch (_error) {
          // The initial viewport still works when storage is unavailable.
        }

        return location;
      } finally {
        clearTimeout(timeout);
        lifecycleSignal?.removeEventListener('abort', abortForLifecycle);
      }
    }

    styleProvince(feature) {
      const isShippable = feature.properties.shippable;
      return {
        color: this.cssVariable('--delivery-zone-border', '#424242'),
        weight: isShippable ? 1.4 : 1,
        opacity: isShippable ? 0.88 : 0.5,
        fillColor: this.cssVariable(isShippable ? '--delivery-zone-canada' : '--delivery-zone-muted', isShippable ? '#61d4a6' : '#dfe3df'),
        fillOpacity: isShippable ? 0.22 : 0.14
      };
    }

    cssVariable(name, fallback) {
      return getComputedStyle(this).getPropertyValue(name).trim() || fallback;
    }

    popupMarkup(feature) {
      const name = this.escapeHtml(window.JomoDeliveryData.zoneDisplayName(feature.properties.name));
      const description = this.escapeHtml(feature.properties.description);
      return `<p class="delivery-zone-map__popup-name">${name}</p><p class="delivery-zone-map__popup-copy">${description}</p>`;
    }

    provincePopupMarkup(feature) {
      const name = this.escapeHtml(feature.properties.name);
      const description = feature.properties.shippable
        ? this.parcelShippingCopy(this.parcelEligibilityCopy())
        : this.territoryMessage(feature.properties.name);
      return `<p class="delivery-zone-map__popup-name">${name}</p><p class="delivery-zone-map__popup-copy">${this.escapeHtml(description)}</p>`;
    }

    selectProvince(name) {
      this.provinceLayers.forEach((layer, layerName) => {
        const feature = this.canadaZones.features.find((item) => item.properties.name === layerName);
        if (layerName === name) {
          layer.setStyle({ weight: 2.8, fillOpacity: feature?.properties.shippable ? 0.42 : 0.28 });
          if (layer.bringToFront) layer.bringToFront();
        } else {
          this.resetProvinceStyle(layerName);
        }
      });

      this.featureLayers.forEach((layer) => layer.bringToFront?.());
    }

    selectZone(name) {
      this.featureLayers.forEach((layer, layerName) => {
        if (layerName === name) {
          layer.setStyle({ weight: 3, fillOpacity: 0.46 });
          if (layer.bringToFront) layer.bringToFront();
        } else {
          this.resetLayerStyle(layerName);
        }
      });
    }

    resetLayerStyle(name) {
      const layer = this.featureLayers.get(name);
      const feature = this.zones.features.find((item) => item.properties.name === name);
      if (layer && feature) layer.setStyle(this.styleFeature(feature));
    }

    resetProvinceStyle(name) {
      const layer = this.provinceLayers.get(name);
      const feature = this.canadaZones.features.find((item) => item.properties.name === name);
      if (layer && feature) layer.setStyle(this.styleProvince(feature));
    }

    bindChecker() {
      if (this.dataset.enableChecker !== 'true' || !this.form) return;

      this.listen(this.form, 'submit', (event) => {
        event.preventDefault();
        if (this.rates) this.rates.hidden = true;
        const query = this.input.value.trim();
        if (!query) {
          this.input.focus();
          this.showResult('error', 'Enter an address', 'Add a Canadian address or postal code to check delivery and shipping.');
          return;
        }
        this.searchAddress(query);
      });

      this.listen(this.locationButton, 'click', () => this.useCurrentLocation());

      const updateClearButton = () => {
        if (this.clearButton) this.clearButton.hidden = !this.input.value;
      };

      this.listen(this.input, 'input', updateClearButton);
      this.listen(this.clearButton, 'click', () => {
        this.input.value = '';
        updateClearButton();
        this.input.focus();
      });
      updateClearButton();
    }

    async searchAddress(query) {
      const generation = this.lifecycleGeneration;
      const selectionRevision = this.selectionRevision;
      const analyticsContext = this.analyticsContext();
      this.setSearching(true);

      try {
        const result = await this.geocode(query, this.lifecycleController?.signal);
        if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision) return;
        if (!result) {
          this.showResult('error', 'Address not found', 'Try including the street number, city, or full Canadian postal code.');
          this.trackDeliveryCheck({
            analyticsContext,
            inputType: this.analyticsInputType(query),
            postalArea: this.analyticsPostalArea(query),
            resultStatus: 'address_not_found'
          });
          return;
        }

        const postalCode = this.extractFullPostalCode(query, result);
        const coverage = this.checkPoint(
          Number(result.lat),
          Number(result.lon),
          result.display_name,
          result.province || result.provinceCode || result.address?.state || result.address?.province || result.address?.['ISO3166-2-lvl4']
        );
        const rateSummary = await this.updateLiveRates({
          coverage,
          postalCode
        }, this.lifecycleController?.signal);
        if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision) return;
        this.trackDeliveryCheck({
          analyticsContext,
          coverage,
          inputType: this.analyticsInputType(query),
          postalArea: this.analyticsPostalArea(query, result),
          resultStatus: 'checked',
          ...rateSummary
        });
      } catch (error) {
        if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision || error?.name === 'AbortError') return;
        console.error('[Delivery zone checker]', error);
        this.showResult('error', 'We could not check that address', 'Please try again shortly or confirm the delivery options at checkout.');
        this.trackDeliveryCheck({
          analyticsContext,
          inputType: this.analyticsInputType(query),
          postalArea: this.analyticsPostalArea(query),
          resultStatus: 'check_error'
        });
      } finally {
        if (this.isCurrentGeneration(generation) && this.selectionRevision === selectionRevision) this.setSearching(false);
      }
    }

    async geocode(query, signal = this.lifecycleController?.signal) {
      const normalizedQuery = query.replace(/\s+/g, ' ').trim();
      const cacheKey = `${GEOCODE_CACHE_PREFIX}${normalizedQuery.toLowerCase()}`;
      const cachedResult = this.readCache(cacheKey);
      if (cachedResult) return cachedResult;

      const postalArea = this.canadianPostalArea(normalizedQuery);
      if (postalArea) {
        const postalResult = await this.geocodePostalArea(postalArea, signal);
        if (postalResult) {
          this.writeCache(cacheKey, postalResult);
          return postalResult;
        }
      }

      const searchQueries = [normalizedQuery];
      const queryWithoutPostalCode = this.removeFullPostalCode(normalizedQuery);

      if (queryWithoutPostalCode && queryWithoutPostalCode !== normalizedQuery) {
        searchQueries.push(queryWithoutPostalCode);
      }

      const searchAttempts = [
        ...searchQueries.map((searchQuery) => ({ searchQuery, countryCodes: 'ca' })),
        { searchQuery: normalizedQuery, countryCodes: '' }
      ];

      for (const { searchQuery, countryCodes } of searchAttempts) {
        const url = new URL(this.dataset.geocoderUrl);
        url.searchParams.set('format', 'jsonv2');
        url.searchParams.set('limit', '1');
        if (countryCodes) url.searchParams.set('countrycodes', countryCodes);
        url.searchParams.set('addressdetails', '1');
        url.searchParams.set('q', searchQuery);

        const response = await fetch(url.toString(), {
          headers: { 'Accept-Language': document.documentElement.lang || 'en-CA' },
          signal
        });
        if (!response.ok) throw new Error(`Geocoder returned ${response.status}`);

        const results = await response.json();
        const result = results[0] || null;

        if (result) {
          this.writeCache(cacheKey, result);
          return result;
        }
      }

      return null;
    }

    extractFullPostalCode(query, result = {}) {
      const candidates = [query, result.postcode, result.address?.postcode];
      const postalCodePattern = /\b([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ])[\s-]?(\d[ABCEGHJ-NPRSTVWXYZ]\d)\b/i;

      for (const candidate of candidates) {
        const match = String(candidate || '').match(postalCodePattern);
        if (match) return `${match[1]} ${match[2]}`.toUpperCase();
      }

      return null;
    }

    removeFullPostalCode(query) {
      const postalCodePattern = /\b[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ][\s-]?\d[ABCEGHJ-NPRSTVWXYZ]\d\b/i;

      return String(query || '')
        .replace(postalCodePattern, '')
        .replace(/\s+,/g, ',')
        .replace(/,\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    canadianPostalArea(query) {
      const compact = query.toUpperCase().replace(/[^A-Z0-9]/g, '');
      const isCanadianPostalCode = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ](?:\d[ABCEGHJ-NPRSTVWXYZ]\d)?$/.test(compact);
      return isCanadianPostalCode ? compact.slice(0, 3) : null;
    }

    async geocodePostalArea(postalArea, signal = this.lifecycleController?.signal) {
      if (!this.dataset.postalGeocoderUrl) return null;

      const baseUrl = this.dataset.postalGeocoderUrl.endsWith('/')
        ? this.dataset.postalGeocoderUrl
        : `${this.dataset.postalGeocoderUrl}/`;
      const response = await fetch(`${baseUrl}${encodeURIComponent(postalArea)}`, { signal });

      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`Postal geocoder returned ${response.status}`);

      const result = await response.json();
      const place = result.places?.[0];
      if (!place) return null;

      return {
        lat: place.latitude,
        lon: place.longitude,
        display_name: `${postalArea} — ${place['place name']}, ${place.state}, Canada`,
        province: place.state,
        provinceCode: place['state abbreviation']
      };
    }

    async updateLiveRates({ coverage, postalCode }, signal = this.lifecycleController?.signal) {
      const noLiveRates = { liveRatesFound: false, rateCount: 0 };
      const generation = this.lifecycleGeneration;
      const selectionRevision = this.selectionRevision;
      if (this.dataset.enableLiveRates !== 'true' || !this.rates) return noLiveRates;
      this.rates.hidden = true;

      if (!coverage?.covered || !postalCode) return noLiveRates;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', signal });
        this.assertCurrentGeneration(generation, signal, selectionRevision);
        if (!cartResponse.ok) throw new Error(`Cart returned ${cartResponse.status}`);

        const cart = await cartResponse.json();
        this.assertCurrentGeneration(generation, signal, selectionRevision);
        if (!cart.item_count) return noLiveRates;

        const requiresShipping = cart.items?.some((item) => item.requires_shipping);
        if (!requiresShipping) return noLiveRates;

        const province = coverage.province?.properties?.name;
        if (!province) throw new Error('Province could not be determined');

        this.showRatesMessage('Calculating live rates…', 'Using the items currently in your cart and this postal code.', [], true);
        const rates = await this.fetchCartShippingRates({ root, postalCode, province }, signal);
        this.assertCurrentGeneration(generation, signal, selectionRevision);
        if (!rates.length) {
          this.rates.hidden = true;
          return noLiveRates;
        }

        const itemLabel = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        this.showRatesMessage(
          'Live rates for your current cart',
          `Based on ${itemLabel} and ${postalCode}. The final rate will be confirmed at checkout.`,
          rates
        );
        return { liveRatesFound: true, rateCount: rates.length };
      } catch (error) {
        if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision || signal?.aborted || error?.name === 'AbortError') return noLiveRates;
        console.error('[Delivery zone live rates]', error);
        this.rates.hidden = true;
        return noLiveRates;
      }
    }

    async fetchCartShippingRates({ root, postalCode, province }, signal = this.lifecycleController?.signal) {
      const parameters = new URLSearchParams({
        'shipping_address[zip]': postalCode,
        'shipping_address[country]': 'Canada',
        'shipping_address[province]': province
      });
      const prepareResponse = await fetch(`${root}cart/prepare_shipping_rates.json?${parameters}`, {
        method: 'POST',
        credentials: 'same-origin',
        signal
      });
      if (!prepareResponse.ok) throw new Error(`Shipping rate preparation returned ${prepareResponse.status}`);

      for (let attempt = 0; attempt < 14; attempt += 1) {
        const response = await fetch(`${root}cart/async_shipping_rates.json?${parameters}`, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error(`Shipping rates returned ${response.status}`);

        const text = await response.text();
        if (text !== 'null') return JSON.parse(text).shipping_rates || [];
        await this.wait(450, signal);
      }

      throw new Error('Shipping rate calculation timed out');
    }

    showRatesMessage(title, copy, rates = [], isLoading = false) {
      if (!this.rates) return;

      const hasLiveRates = rates.length > 0 && !isLoading;
      this.rates.classList.toggle('is-loading', isLoading);
      this.rates.classList.toggle('has-live-rates', hasLiveRates);
      this.ratesTitle.textContent = hasLiveRates && this.resultTitle?.textContent
        ? this.resultTitle.textContent
        : title;
      this.ratesCopy.textContent = copy;
      this.ratesList.replaceChildren();
      this.ratesToggle.hidden = true;
      this.ratesToggle.dataset.hiddenRateCount = '0';
      this.ratesToggle.setAttribute('aria-expanded', 'false');

      rates.forEach((rate, index) => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('strong');
        const rateName = rate.presentment_name || rate.name || 'Shipping';
        name.textContent = rateName.trim().toLowerCase() === 'local delivery'
          ? 'On Demand Delivery'
          : rateName;
        price.textContent = this.formatShippingRate(rate);
        if (index >= VISIBLE_RATE_COUNT) {
          item.dataset.zoneRateExtra = '';
          item.hidden = true;
        }
        item.append(name, price);
        this.ratesList.appendChild(item);
      });

      const hiddenRateCount = Math.max(0, rates.length - VISIBLE_RATE_COUNT);
      if (hiddenRateCount > 0) {
        this.ratesToggle.dataset.hiddenRateCount = String(hiddenRateCount);
        this.ratesToggleLabel.textContent = this.additionalRatesLabel(hiddenRateCount);
        this.ratesToggle.hidden = false;
      }

      this.rates.hidden = false;
    }

    toggleAdditionalRates() {
      if (!this.ratesToggle || !this.ratesList) return;

      const isExpanded = this.ratesToggle.getAttribute('aria-expanded') === 'true';
      const willExpand = !isExpanded;
      const hiddenRateCount = Number(this.ratesToggle.dataset.hiddenRateCount) || 0;

      this.ratesList.querySelectorAll('[data-zone-rate-extra]').forEach((item) => {
        item.hidden = !willExpand;
      });
      this.ratesToggle.setAttribute('aria-expanded', String(willExpand));
      this.ratesToggleLabel.textContent = willExpand
        ? 'Show fewer rates'
        : this.additionalRatesLabel(hiddenRateCount);
    }

    additionalRatesLabel(count) {
      return `Show ${count} more ${count === 1 ? 'rate' : 'rates'}`;
    }

    formatShippingRate(rate) {
      const amount = Number(rate.price);
      if (!Number.isFinite(amount) || amount === 0) return 'Free';

      const currency = rate.currency || window.Shopify?.currency?.active || 'CAD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      }).format(amount);
    }

    wait(milliseconds, signal = this.lifecycleController?.signal) {
      return new Promise((resolve, reject) => {
        if (signal?.aborted) {
          reject(new DOMException('The operation was aborted.', 'AbortError'));
          return;
        }

        const timeout = setTimeout(() => {
          signal?.removeEventListener('abort', abort);
          resolve();
        }, milliseconds);
        const abort = () => {
          clearTimeout(timeout);
          reject(new DOMException('The operation was aborted.', 'AbortError'));
        };
        signal?.addEventListener('abort', abort, { once: true });
      });
    }

    assertCurrentGeneration(generation, signal = this.lifecycleController?.signal, selectionRevision = this.selectionRevision) {
      if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision || signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
    }

    readCache(key) {
      try {
        const cached = JSON.parse(localStorage.getItem(key));
        if (!cached || Date.now() - cached.savedAt > 30 * 24 * 60 * 60 * 1000) return null;
        return cached.result;
      } catch (_error) {
        return null;
      }
    }

    writeCache(key, result) {
      try {
        localStorage.setItem(key, JSON.stringify({ result, savedAt: Date.now() }));
      } catch (_error) {
        // Storage may be disabled; the checker still works without caching.
      }
    }

    analyticsInputType(query) {
      return this.canadianPostalArea(query) ? 'postal_code' : 'address';
    }

    analyticsPostalArea(query, result = {}) {
      const postalCode = this.extractFullPostalCode(query, result);
      if (postalCode) return postalCode.replace(/\s/g, '').slice(0, 3);
      return this.canadianPostalArea(query) || '';
    }

    trackDeliveryCheck({
      coverage = null,
      inputType,
      postalArea = '',
      resultStatus,
      liveRatesFound = false,
      rateCount = 0,
      analyticsContext = this.analyticsContext()
    }) {
      const service = coverage?.service || 'unknown';
      const zoneName = coverage?.zone?.properties?.name
        || (service === 'shipping' ? 'Canada wide' : service === 'unknown' ? 'Not found' : 'Outside coverage');
      const eventData = {
        ...analyticsContext,
        input_type: inputType,
        result_status: resultStatus,
        coverage_found: Boolean(coverage?.covered),
        delivery_service: service,
        delivery_zone: zoneName,
        province: coverage?.province?.properties?.name || 'Not available',
        postal_area: postalArea || 'Not available',
        live_rates_found: Boolean(liveRatesFound),
        rate_count: Number(rateCount) || 0,
        detection_comparison: this.detectionComparison(coverage, analyticsContext, resultStatus)
      };

      try {
        window.JomoAnalytics?.deliveryEvent?.('delivery_zone_checked', eventData);
      } catch (_error) {}
    }

    detectionComparison(coverage, context, resultStatus) {
      const detectedService = context.detected_delivery_service;
      if (resultStatus !== 'checked' || !coverage?.service || !detectedService || detectedService === 'unknown') return 'unknown';
      if (coverage.service !== detectedService) return 'different_area';
      const detectedArea = detectedService === 'local' ? context.detected_delivery_zone : context.detected_province;
      const checkedArea = detectedService === 'local' ? coverage.zone?.properties?.name : coverage.province?.properties?.name;
      if (!checkedArea || !detectedArea || ['Not available', 'Not found', 'Outside coverage'].includes(detectedArea)) return 'unknown';
      return checkedArea === detectedArea ? 'same_area' : 'different_area';
    }

    useCurrentLocation() {
      const generation = this.lifecycleGeneration;
      const selectionRevision = this.selectionRevision;
      const analyticsContext = this.analyticsContext();
      if (this.rates) this.rates.hidden = true;
      if (!navigator.geolocation) {
        this.showResult('error', 'Location is unavailable', 'Enter your address or postal code instead.');
        this.trackDeliveryCheck({ analyticsContext, inputType: 'current_location', resultStatus: 'location_unavailable' });
        return;
      }

      this.setSearching(true, true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision) return;
          const coverage = this.checkPoint(position.coords.latitude, position.coords.longitude, 'Your current location');
          const rateSummary = await this.updateLiveRates({ coverage, postalCode: null }, this.lifecycleController?.signal);
          if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision) return;
          this.trackDeliveryCheck({
            analyticsContext,
            coverage,
            inputType: 'current_location',
            resultStatus: 'checked',
            ...rateSummary
          });
          this.setSearching(false, true);
        },
        () => {
          if (!this.isCurrentGeneration(generation) || this.selectionRevision !== selectionRevision) return;
          this.showResult('error', 'We could not access your location', 'Allow location access or enter your address or postal code instead.');
          this.trackDeliveryCheck({ analyticsContext, inputType: 'current_location', resultStatus: 'location_denied' });
          this.setSearching(false, true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    checkPoint(latitude, longitude, label, provinceHint = '') {
      this.userAdjustedViewport = true;
      this.lastCheckedInput = this.input?.value.trim() || '';
      const point = [longitude, latitude];
      const matchingZone = this.zones.features.find((feature) => this.featureContainsPoint(feature, point));
      const matchingProvince = this.findProvinceByHint(provinceHint)
        || this.canadaZones.features.find((feature) => this.featureContainsPoint(feature, point));
      const isCovered = Boolean(matchingZone || matchingProvince?.properties.shippable);

      this.showPoint(latitude, longitude, label, isCovered, isCovered);
      if (!isCovered) this.showAllCanada();

      if (matchingZone) {
        const name = matchingZone.properties.name;
        const province = matchingProvince || this.findProvinceByHint('ON');
        this.clearSelectedProvince();
        this.selectZone(name);
        this.showResult('success', name, matchingZone.properties.description);
        return this.rememberCoverage({ covered: true, service: 'local', zone: matchingZone, province });
      }

      this.clearSelectedZone();

      if (matchingProvince?.properties.shippable) {
        const name = matchingProvince.properties.name;
        this.selectProvince(name);
        this.showResult(
          'success',
          name,
          this.parcelEligibilityCopy(),
          true
        );
        return this.rememberCoverage({ covered: true, service: 'shipping', province: matchingProvince });
      } else if (matchingProvince) {
        this.selectProvince(matchingProvince.properties.name);
        this.showResult(
          'outside',
          this.territoryMessage(matchingProvince.properties.name),
          TERRITORY_ADDRESS_PROMPT
        );
        return this.rememberCoverage({ covered: false, service: 'territory', province: matchingProvince });
      } else {
        this.clearSelectedProvince();
        this.showResult('outside', CANADA_ONLY_TITLE, CANADA_ADDRESS_PROMPT);
        return this.rememberCoverage({ covered: false, service: 'outside', province: null });
      }
    }

    rememberCoverage(coverage) {
      const selection = {
        service: coverage.service,
        zoneName: coverage.zone?.properties?.name || '',
        provinceName: coverage.province?.properties?.name || '',
        savedAt: Date.now()
      };

      window.JomoDeliveryData.setSelection(selection);

      this.sharedCoverage = selection;
      this.publishingCoverage = true;
      try {
        window.dispatchEvent(new CustomEvent('jomo:delivery-zone-change', { detail: selection }));
      } finally {
        this.publishingCoverage = false;
      }
      return coverage;
    }

    showPoint(latitude, longitude, label, isInside, focusPoint = true) {
      if (!this.map || !window.L) return;

      if (this.searchMarker) this.searchMarker.remove();
      this.searchMarker = window.L.circleMarker([latitude, longitude], {
        radius: 8,
        color: '#ffffff',
        weight: 3,
        fillColor: isInside ? this.cssVariable('--delivery-zone-border', '#424242') : '#e32c2b',
        fillOpacity: 1
      }).addTo(this.map);
      this.searchMarker.bindTooltip(this.escapeHtml(label), { direction: 'top', offset: [0, -8] });
      if (focusPoint) this.map.setView([latitude, longitude], 12, this.mapMotionOptions());
    }

    showAllCanada() {
      if (!this.map || !this.canadaLayer) return;
      this.map.fitBounds(this.getShippingCoverageBounds(), this.mapMotionOptions({ padding: [20, 20], animate: true }));
    }

    mapMotionOptions(options = {}) {
      if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return options;
      return { ...options, animate: false };
    }

    getShippingCoverageBounds() {
      if (!this.map || !this.canadaLayer || !window.L) return this.canadaLayer?.getBounds();

      const bounds = window.L.latLngBounds();
      this.provinceLayers.forEach((layer, name) => {
        const feature = this.canadaZones.features.find((item) => item.properties.name === name);
        if (feature?.properties.shippable) bounds.extend(layer.getBounds());
      });

      return bounds.isValid() ? bounds : this.canadaLayer.getBounds();
    }

    featureContainsPoint(feature, point) {
      if (feature.geometry.type === 'Polygon') {
        return this.polygonContainsPoint(feature.geometry.coordinates, point);
      }

      if (feature.geometry.type === 'MultiPolygon') {
        return feature.geometry.coordinates.some((polygon) => this.polygonContainsPoint(polygon, point));
      }

      return false;
    }

    findProvinceByHint(value) {
      const normalized = String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/^CA[-_\s]*/, '')
        .replace(/[^A-Z]/g, '');
      const name = PROVINCE_ALIASES[normalized];
      return name ? this.canadaZones.features.find((feature) => feature.properties.name === name) : null;
    }

    polygonContainsPoint(rings, point) {
      if (!rings.length || !this.ringContainsPoint(rings[0], point)) return false;
      return !rings.slice(1).some((ring) => this.ringContainsPoint(ring, point));
    }

    ringContainsPoint(ring, point) {
      let inside = false;
      const [x, y] = point;

      for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
        const [xi, yi] = ring[index];
        const [xj, yj] = ring[previous];

        if (this.pointOnSegment(point, ring[previous], ring[index])) return true;

        const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersects) inside = !inside;
      }

      return inside;
    }

    pointOnSegment(point, start, end) {
      const [x, y] = point;
      const [x1, y1] = start;
      const [x2, y2] = end;
      const squaredLength = (x2 - x1) ** 2 + (y2 - y1) ** 2;

      // GeoJSON rings repeat their first coordinate at the end. Treat that
      // zero-length closing segment as a single point instead of matching
      // every possible location.
      if (squaredLength <= 1e-18) {
        return Math.abs(x - x1) <= 1e-9 && Math.abs(y - y1) <= 1e-9;
      }

      const crossProduct = (y - y1) * (x2 - x1) - (x - x1) * (y2 - y1);
      if (Math.abs(crossProduct) > 1e-9) return false;

      const dotProduct = (x - x1) * (x2 - x1) + (y - y1) * (y2 - y1);
      if (dotProduct < 0) return false;

      return dotProduct <= squaredLength;
    }

    clearSelectedZone() {
      this.featureLayers.forEach((_layer, name) => this.resetLayerStyle(name));
    }

    clearSelectedProvince() {
      this.provinceLayers.forEach((_layer, name) => this.resetProvinceStyle(name));
      this.featureLayers.forEach((layer) => layer.bringToFront?.());
    }

    territoryMessage(name) {
      return `We don’t currently ship to ${name || 'Canada’s territories'}.`;
    }

    parcelEligibilityCopy(approximate = false) {
      const confirmation = approximate ? ' Enter your address or postal code to confirm availability.' : '';
      return `Plants and planters 7 inches or smaller are eligible. ${PARCEL_ORDER_CUTOFF}${confirmation} Final availability, rates, and delivery estimates are confirmed at checkout.`;
    }

    parcelShippingCopy(copy) {
      const shipmentMessage = window.JomoShipmentSchedule?.getMessage?.();
      return shipmentMessage ? `${shipmentMessage}. ${copy}` : copy;
    }

    renderShipmentDate() {
      if (!this.resultDispatch) return;
      const message = this.parcelResultCopy && this.result?.dataset.state === 'success'
        ? window.JomoShipmentSchedule?.getMessage?.()
        : '';
      this.resultDispatch.textContent = message ? `${message}.` : '';
      this.resultDispatch.hidden = !message;
    }

    refreshShipmentSchedule() {
      if (!this.isConnectedAndInitialized) return;
      if (this.parcelResultCopy && this.result && !this.result.hidden && this.result.dataset.state === 'success') {
        this.renderShipmentDate();
      }
      this.provinceLayers.forEach((layer, name) => {
        if (!layer.isPopupOpen?.()) return;
        const feature = this.canadaZones?.features.find((item) => item.properties.name === name);
        if (feature?.properties.shippable) layer.setPopupContent(() => this.provincePopupMarkup(feature));
      });
    }

    showResult(state, title, copy, parcelShipping = false) {
      this.parcelResultCopy = parcelShipping ? copy : null;
      if (!this.result) return;
      this.result.dataset.state = state;
      this.resultTitle.textContent = window.JomoDeliveryData.zoneDisplayName(title);
      this.resultCopy.textContent = copy;
      this.result.hidden = false;
      this.renderShipmentDate();
    }

    setSearching(isSearching, locationOnly = false) {
      this.isSearching = isSearching;
      if (isSearching) {
        this.parcelResultCopy = null;
        this.renderShipmentDate();
      }
      this.classList.toggle('is-searching', isSearching && !locationOnly);
      if (this.submitButton) this.submitButton.disabled = isSearching;
      if (this.input) this.input.disabled = isSearching;
      if (this.locationButton) this.locationButton.disabled = isSearching;
      if (this.clearButton) this.clearButton.disabled = isSearching;
      this.form?.setAttribute('aria-busy', String(isSearching));
    }

    showMapFallback() {
      if (this.loading) this.loading.hidden = true;
      if (this.canvas) this.canvas.hidden = true;
      if (this.fallback) this.fallback.hidden = false;
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = value || '';
      return element.innerHTML;
    }
  }

  if (!customElements.get('delivery-zone-map')) {
    customElements.define('delivery-zone-map', DeliveryZoneMap);
  }
})();
